import React, { useState, useEffect, useCallback } from 'react';
import {
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
} from '@mui/material';
import { scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { Group } from '@visx/group';
import { AxisBottom } from '@visx/axis';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { max, min } from 'd3-array';
import Truncate from 'react-truncate';
import PropTypes from 'prop-types';

import {
  getParams,
  thousandFormat,
} from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  barHeight,
  axis,
} from '../../../../Assets/js/constant';

// accessors
const x = (d) => d.ForecastAmount;
const xF = (d) => d.ActualFeesInvoicedMTD;
const y = (d, key) => d[key];

let tooltipTimeout;

const MiddleChart = ({
  detailData,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  selectedMiddleItems,
  filterName,
  maxNameLength,
  handleFilter,
  getMiddleChartHeight,
}) => {
  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [data, setData] = useState([]);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [selectedRects, setSelectedRects] = useState([]);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dataArray = [];

    let _selectedMonths =
      selectedTopItems.length > 0 ? [] : selectedMonths.slice();
    selectedTopItems.forEach((item) => {
      _selectedMonths.push(item['month']);
    });

    detailData &&
      detailData.forEach((d) => {
        if (
          _selectedMonths.length === 0 ||
          _selectedMonths.indexOf(new Date(d.Date).getMonth()) > -1
        ) {
          let item = d[filterName];
          if (item) {
            dictData[item] = {};
            dictData[item][filterName] = item;
            dictData[item].ForecastAmount = 0;
            dictData[item].ActualFeesInvoicedMTD = 0;
          }
        }
      });

    detailData &&
      detailData.forEach((d) => {
        if (
          _selectedMonths.length === 0 ||
          _selectedMonths.indexOf(new Date(d.Date).getMonth()) > -1
        ) {
          let item = d[filterName];
          if (item) {
            dictData[item].ForecastAmount += Number(d.ForecastAmount);
            dictData[item].ActualFeesInvoicedMTD += Number(
              d.ActualFeesInvoicedMTD,
            );
          }
        }
      });

    Object.keys(dictData).map((key) => dataArray.push(dictData[key]));
    dataArray.sort((a, b) =>
      a.ActualFeesInvoicedMTD > b.ActualFeesInvoicedMTD ? 1 : -1,
    );

    getMiddleChartHeight(dataArray.length > 7 ? 229 : dataArray.length > 3 ? 23 * dataArray.length + 68 : 137);
    setData(dataArray);
  }, [detailData, filterName, selectedMonths, selectedTopItems, getMiddleChartHeight]);

  const getColor = useCallback((data) => {
    const activeColor =
      xF(data) < x(data) ? negativeActiveColor : positiveActiveColor;
    const disableColor =
      xF(data) < x(data) ? negativeDisableColor : positiveDisableColor;

    if (selectedMiddleItems.length === 0) return activeColor;
    if (selectedMiddleItems.indexOf(data[filterName]) > -1) return activeColor;
    return disableColor;
  }, [selectedMiddleItems, filterName]);

  const handleFilterChange = useCallback(async (event) => {
    const filterName = event.target.value;
    handleFilter({ filterName });
  }, [handleFilter]);

  const handleElement = useCallback((event, element, item = null) => {
    let _selectedBars = [],
      _selectedRects = [],
      _selectedMiddleItems = [];
    let exist = false,
      index = NaN;

    switch (element) {
      case 'bar':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedMiddleItems = selectedMiddleItems.slice();
          _selectedRects = selectedRects.slice();

          for (let i = 0; i < selectedBars.length; i++) {
            if (selectedBars[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('barActive');
            _selectedBars.push(event.target);
            _selectedMiddleItems.push(item);
          } else {
            event.target.classList.remove('barActive');
            _selectedBars.splice(index, 1);
            _selectedMiddleItems.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
            if (selectedBar === event.target) exist = true;
          });

          selectedRects.forEach((selectedRect) => {
            selectedRect.classList.remove('bkgActive');
          });

          if (exist && selectedBars.length === 1) {
            _selectedBars = [];
            _selectedRects = [];
            _selectedMiddleItems = [];
          } else {
            event.target.classList.add('barActive');
            _selectedBars = [event.target];
            _selectedRects = [];
            _selectedMiddleItems = [item];
          }
        }

        break;

      case 'rect':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedMiddleItems = selectedMiddleItems.slice();
          _selectedRects = selectedRects.slice();

          for (let i = 0; i < selectedRects.length; i++) {
            if (selectedRects[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('bkgActive');
            _selectedRects.push(event.target);
            _selectedMiddleItems.push(item);
          } else {
            event.target.classList.remove('bkgActive');
            _selectedRects.splice(index, 1);
            _selectedMiddleItems.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
          });
          selectedRects.forEach((selectedRect) => {
            selectedRect.classList.remove('bkgActive');
            if (selectedRect === event.target) exist = true;
          });

          if (exist && selectedRects.length === 1) {
            _selectedBars = [];
            _selectedRects = [];
            _selectedMiddleItems = [];
          } else {
            event.target.classList.add('bkgActive');
            _selectedBars = [];
            _selectedRects = [event.target];
            _selectedMiddleItems = [item];
          }
        }

        break;

      default:
        break;
    }

    setSelectedBars(_selectedBars);
    setSelectedRects(_selectedRects);

    handleFilter({ selectedMiddleItems: _selectedMiddleItems });
  }, [selectedBars, selectedRects, selectedMiddleItems, handleFilter]);

  const deSelectAll = useCallback(() => {
    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    selectedRects.forEach((selectedRect) => {
      selectedRect.classList.remove('bkgActive');
    });

    setSelectedBars([]);
    setSelectedRects([]);

    handleFilter({ selectedMiddleItems: [] });
  }, [selectedBars, selectedRects, handleFilter]);

  const showTooltipHandler = useCallback((event, data, isBar = true) => {
    let tooltipDataObj, top, left;
    top =
      event.nativeEvent.layerY -
      document.getElementById('fee-middle-chart-content').scrollTop +
      30;
    left =
      window.innerWidth / 2 < event.nativeEvent.layerX
        ? event.nativeEvent.layerX - 230
        : event.nativeEvent.layerX + 50;
    tooltipDataObj = data;
    tooltipDataObj['isBar'] = isBar;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
    }

    showTooltip({
      tooltipData: tooltipDataObj,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');
    setHoverBar(null);

    hideTooltip();
  }, [hoverBar, hideTooltip]);

  // Component mount effect
  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [prepareData, onResize]);

  // Component update effect for prop changes
  useEffect(() => {
    prepareData();
  }, [detailData, selectedYears, selectedMonths, selectedTopItems, filterName, prepareData]);

  const count = data.length;
  const width =
    window.innerWidth - maxNameLength * 6.6 - 35 > 0
      ? window.innerWidth - maxNameLength * 6.6 - 35
      : 0;
  const height = count * 23;
  const margin = {
    top: -10,
    right: 0,
    bottom: 0,
    left: 0,
  };
  const { pageYOffset, xMax } = getParams(window, width, height, margin);

  let maxValue = 0;
  let minValue = 0;
  if (count > 0) {
    maxValue = Math.max(
      0,
      max(data, (d) => x(d)),
      max(data, (d) => xF(d)),
    );
    minValue = Math.min(
      0,
      min(data, (d) => x(d)),
      min(data, (d) => xF(d)),
    );
    maxValue = Math.ceil(maxValue / 1000) * 1000;
    minValue = Math.floor(minValue / 1000) * 1000;
  }

  // scales
  const xScale = scaleLinear({
    domain: [minValue, maxValue],
    range: [0, xMax],
    nice: true,
  });

  return (
    <div id="fee-middle-chart" style={{ height: data.length > 7 ? 229 : data.length > 3 ? 23 * data.length + 68 : 137 }}>
      <div className="wrapper mt-10 mb-10">
        <FormControlLabel
          control={
            <FormControl variant="standard">
              <Select
                onChange={handleFilterChange}
                sx={{
                  '& .MuiInputBase-input': {
                    padding: 0,
                    paddingRight: 24,
                    fontWeight: 'bold',
                    fontFamily: 'Verdana !important',
                    fontSize: '10pt',
                  },
                }}
                value={filterName}
                name="filterName"
              >
                <MenuItem value="Director">Director</MenuItem>
                <MenuItem value="Supervisor">Associate</MenuItem>
                <MenuItem value="ProjectManager">Project Leader</MenuItem>
                <MenuItem value="ProjectType">Project Type</MenuItem>
                <MenuItem value="ProjectSubType">Project Sub Type</MenuItem>
                <MenuItem value="ProjectSourceOne">Project Source 1</MenuItem>
                <MenuItem value="ProjectSourceTwo">Project Source 2</MenuItem>
              </Select>
            </FormControl>
          }
          label="By&nbsp;"
          labelPlacement="start"
          sx={{
            marginLeft: '0px !important',
            '& .MuiTypography-root': {
              marginTop: '2px',
              fontWeight: 'bold',
              fontFamily: 'Verdana !important',
              fontSize: '10pt',
            },
          }}
        />
      </div>

      {count > 0 ? (
        <>
          <Grid
            container
            wrap="nowrap"
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'flex-start',
              width: '100%',
              maxHeight: 190,
              overflowY: 'auto',
              borderBottom: 'solid 1px gray',
              borderTop: 'solid 1px gray',
              fontSize: 12,
            }}
            id="fee-middle-chart-content"
          >
            <div
              style={{
                width: maxNameLength * 6.6,
                flex: `0 0 ${maxNameLength * 6.6}px`,
                borderRight: '1px solid darkgray',
              }}
            >
              {data
                .slice()
                .reverse()
                .map((d, i) => {
                  return (
                    <p
                      key={i}
                      style={{
                        marginTop: 0,
                        marginBottom: 0,
                        height: 23,
                        paddingTop: 1,
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      className="grayHover"
                      onClick={(event) =>
                        handleElement(event, 'rect', d[filterName])
                      }
                    >
                      <Truncate
                        ellipsis={<span>...</span>}
                      >
                        {d[filterName]}
                      </Truncate>
                    </p>
                  );
                })}
            </div>

            <div>
              <svg width={width} height={height}>
                <rect
                  width={width}
                  height={height}
                  fill="white"
                  onClick={deSelectAll}
                />
                <Group top={-1} left={margin.left}>
                  {data.map((d, i) => {
                    const barWidth = d
                      ? Math.abs(xScale(xF(d)) - xScale(0))
                      : 0;
                    const barHeight = 18;
                    const barX = d ? xScale(Math.min(0, xF(d))) : 0;
                    const barY = 23 * (data.length - i - 1) + 4;

                    const lineX = xScale(Math.max(0, x(d)));
                    const lineY = 23 * (data.length - i - 1);
                    const lineHeight = 23;

                    return (
                      <Group key={`${filterName}-${y(d, filterName)}-${i}`}>
                        <Bar
                          x={barX}
                          y={barY}
                          width={barWidth}
                          height={barHeight}
                          fill={getColor(d)}
                          onClick={(event) => {
                            handleElement(event, 'bar', d[filterName]);
                          }}
                          onMouseMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(event, d);
                          }}
                          onMouseLeave={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                          onTouchMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(event, d);
                          }}
                          onTouchEnd={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                        />

                        <Bar
                          x={lineX}
                          y={lineY}
                          width={0.5}
                          stroke="black"
                          strokeWidth={0.5}
                          height={lineHeight}
                          fill="black"
                          onMouseMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(event, d, false);
                          }}
                          onMouseLeave={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                          onTouchMove={(event) => {
                            if (tooltipTimeout) clearTimeout(tooltipTimeout);
                            showTooltipHandler(event, d, false);
                          }}
                          onTouchEnd={(event) => {
                            tooltipTimeout = setTimeout(() => {
                              hideTooltipHandler();
                            }, 300);
                          }}
                        />
                      </Group>
                    );
                  })}
                </Group>
              </svg>

              {tooltipOpen && (
                <TooltipWithBounds
                 
                  top={tooltipTop + pageYOffset}
                  left={tooltipLeft}
                >
                  {tooltipData.isBar ? (
                    <div className="color-black">
                      <div className="pdv-5">
                        <strong>{tooltipData[filterName]}</strong>
                      </div>
                      <div className="ft-12">
                        <strong>Fees Invoiced</strong>: $
                        {thousandFormat(xF(tooltipData))}
                      </div>
                      <div className="ft-12">
                        <strong>Fee Forecast</strong>: $
                        {thousandFormat(x(tooltipData))}
                      </div>
                    </div>
                  ) : (
                    <div className="color-black">
                      <strong>Forecast Fees</strong>: $
                      {thousandFormat(x(tooltipData))}
                    </div>
                  )}
                </TooltipWithBounds>
              )}
            </div>
          </Grid>

          <Grid container style={axis}>
            <svg
              width={window.innerWidth - 16}
              height={barHeight}
              style={{ overflow: 'visible' }}
            >
              <rect
                x={0}
                y={0}
                width={window.innerWidth - 16}
                height={barHeight}
                fill="transparent"
                onClick={(event) => {
                  deSelectAll();
                }}
              />
              <AxisBottom
                scale={xScale}
                top={0}
                left={maxNameLength * 6.6}
                hideAxisLine={true}
                stroke="black"
                tickStroke="#a9a9a9"
                tickFormat={(value, index) => value / 1000}
                tickLabelProps={(value, index) => ({
                  fill: 'black',
                  fontSize: 11,
                  textAnchor: 'middle',
                  dy: '0.2em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text {...tickProps} onClick={deSelectAll}>
                    $
                    {Math.abs(formattedValue) < 1000
                      ? `${formattedValue}K`
                      : `${formattedValue / 1000}M`}
                  </text>
                )}
              />
            </svg>
          </Grid>
        </>
      ) : (
        <div style={{ height: 137 }} className="well">
          No data
        </div>
      )}
    </div>
  );
};

MiddleChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedMiddleItems: PropTypes.array.isRequired,
  filterName: PropTypes.string.isRequired,
  maxNameLength: PropTypes.number.isRequired,

  handleFilter: PropTypes.func.isRequired,
  getMiddleChartHeight: PropTypes.func.isRequired,
};

export default MiddleChart;
